import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner, Form, Table } from 'react-bootstrap';
import { ApiGet } from '../../../../ApiServices/ApiServices';
import { toast } from 'react-toastify';
import '../../../../styles/tables.css';

const Payments = () => {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    paymentMethod: '',
    paymentStatus: '',
    fromDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    searchValue: ''
  });

  // Summary
  const [summary, setSummary] = useState({
    total: 0,
    cash: 0,
    card: 0,
    upi: 0,
    count: 0
  });

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: pagination.page - 1,
        pageSize: pagination.pageSize
      };

      // Pass null if empty, otherwise pass the value
      params.paymentMethod = filters.paymentMethod || null;
      params.paymentStatus = filters.paymentStatus || null;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.searchValue) params.searchValue = filters.searchValue;

      const response = await ApiGet('/api/cashier/order_payments/filter', params);

      if (response.success) {
        const data = response.success?.data?.data || response.success?.data || {};
        const records = data?.records || data || [];
        setPayments(Array.isArray(records) ? records : []);
        setPagination(prev => ({
          ...prev,
          totalRecords: data?.totalRecords || records.length || 0,
          totalPages: data?.totalPages || Math.ceil((records.length || 0) / pagination.pageSize) || 0
        }));
        calculateSummary(Array.isArray(records) ? records : []);
      }
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (data) => {
    const sum = data.reduce((acc, p) => {
      acc.total += p.amount || 0;
      acc.count++;
      if (p.paymentMethod === 'CASH') acc.cash += p.amount || 0;
      if (p.paymentMethod === 'CARD') acc.card += p.amount || 0;
      if (p.paymentMethod === 'UPI') acc.upi += p.amount || 0;
      return acc;
    }, { total: 0, cash: 0, card: 0, upi: 0, count: 0 });
    setSummary(sum);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchPayments();
  };

  const handleClear = () => {
    setFilters({
      paymentMethod: '',
      paymentStatus: '',
      fromDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0],
      searchValue: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchPayments(), 0);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getMethodBadge = (method) => {
    const config = {
      'CASH': { bg: 'success', icon: 'bi-cash-coin' },
      'CARD': { bg: 'primary', icon: 'bi-credit-card' },
      'UPI': { bg: 'info', icon: 'bi-phone' }
    };
    const { bg, icon } = config[method] || { bg: 'secondary', icon: 'bi-wallet2' };
    return <Badge bg={bg}><i className={`bi ${icon} me-1`}></i>{method || 'N/A'}</Badge>;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'SUCCESS': 'success',
      'COMPLETED': 'success',
      'PENDING': 'warning',
      'FAILED': 'danger',
      'REFUNDED': 'info'
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status || 'N/A'}</Badge>;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 fw-bold" style={{ color: '#d74440' }}>
          <i className="bi bi-wallet2 me-2"></i>
          Payments
        </h2>
      </div>

      {/* Summary Cards */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <div className="text-muted small">Total Payments</div>
              <div className="fs-4 fw-bold" style={{ color: '#d74440' }}>{formatCurrency(summary.total)}</div>
              <small className="text-muted">{summary.count} transactions</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <div className="text-muted small"><i className="bi bi-cash-coin me-1 text-success"></i>Cash</div>
              <div className="fs-5 fw-bold text-success">{formatCurrency(summary.cash)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <div className="text-muted small"><i className="bi bi-credit-card me-1 text-primary"></i>Card</div>
              <div className="fs-5 fw-bold text-primary">{formatCurrency(summary.card)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <div className="text-muted small"><i className="bi bi-phone me-1 text-info"></i>UPI</div>
              <div className="fs-5 fw-bold text-info">{formatCurrency(summary.upi)}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">Payment Method</Form.Label>
                <Form.Select
                  value={filters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  size="sm"
                >
                  <option value="">All Methods</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="UPI">UPI</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">Payment Status</Form.Label>
                <Form.Select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  size="sm"
                >
                  <option value="">All Status</option>
                  <option value="SUCCESS">Success</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="PENDING">Pending</option>
                  <option value="FAILED">Failed</option>
                  <option value="REFUNDED">Refunded</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={2} md={6}>
              <Form.Group>
                <Form.Label className="small text-muted">Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search payment..."
                  value={filters.searchValue}
                  onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={6}>
              <div className="d-flex gap-2">
                <Button variant="primary" size="sm" onClick={handleSearch} style={{ backgroundColor: '#d74440', borderColor: '#d74440' }}>
                  <i className="bi bi-search me-1"></i> Search
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleClear}>
                  <i className="bi bi-x-circle me-1"></i> Clear
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Payments Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#d74440' }} />
              <div className="mt-2 text-muted">Loading payments...</div>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-wallet2 fs-1 text-muted d-block mb-3" style={{ opacity: 0.5 }}></i>
              <h5 className="text-muted">No payments found</h5>
            </div>
          ) : (
            <>
              <Table responsive hover className="mb-0">
                <thead>
                  <tr style={{ backgroundColor: '#5c2d91' }}>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Payment ID</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Order #</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Customer</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Amount</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Method</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Status</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment, index) => (
                    <tr key={payment.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc', fontSize: '14px' }}>
                      <td className="fw-bold align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{payment.transactionId || `PAY-${payment.id}`}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{payment.orderNumber || payment.orderId || '-'}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{payment.customerName || '-'}</td>
                      <td className="align-middle fw-bold" style={{ padding: '12px 16px', textAlign: 'center', color: '#28a745' }}>{formatCurrency(payment.amount)}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{getMethodBadge(payment.paymentMethod)}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{getStatusBadge(payment.status)}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{formatDateTime(payment.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <small className="text-muted">
                  Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.totalRecords)} of {pagination.totalRecords} payments
                </small>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" disabled={pagination.page <= 1} onClick={() => handlePageChange(pagination.page - 1)}>
                    <i className="bi bi-chevron-left"></i> Previous
                  </Button>
                  <span className="align-self-center px-2">Page {pagination.page} of {pagination.totalPages || 1}</span>
                  <Button variant="outline-secondary" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>
                    Next <i className="bi bi-chevron-right"></i>
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Payments;
